import { useState } from "react";

interface InsufficientBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  requiredAmount: number;
  walletAddress: string;
}

export function InsufficientBalanceModal({
  isOpen,
  onClose,
  currentBalance,
  requiredAmount,
  walletAddress,
}: InsufficientBalanceModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shortfall = requiredAmount - currentBalance;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy address:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative flex w-full max-w-sm flex-col bg-card-light dark:bg-gray-900 rounded-3xl p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          <span className="material-symbols-outlined text-gray-600 dark:text-gray-400">
            close
          </span>
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20">
            <span className="material-symbols-outlined text-red-500 text-4xl">
              account_balance_wallet
            </span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-gray-900 dark:text-white text-xl font-bold text-center mb-2">
          Insufficient Balance
        </h2>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-6">
          You need more USDC to join this challenge
        </p>

        {/* Balance Info */}
        <div className="space-y-3 mb-6">
          <div className="flex justify-between items-center p-3 bg-black/5 dark:bg-black/20 rounded-xl">
            <span className="text-gray-600 dark:text-gray-400 text-sm">Current Balance</span>
            <span className="text-gray-900 dark:text-white font-bold">${currentBalance.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-black/5 dark:bg-black/20 rounded-xl">
            <span className="text-gray-600 dark:text-gray-400 text-sm">Required Amount</span>
            <span className="text-gray-900 dark:text-white font-bold">${requiredAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-700/50">
            <span className="text-red-700 dark:text-red-300 text-sm font-medium">Need to Deposit</span>
            <span className="text-red-700 dark:text-red-300 font-bold">${shortfall.toFixed(2)}</span>
          </div>
        </div>

        {/* Wallet Address */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400 text-xs mb-2">Deposit to (Base Network)</p>
          <div className="flex items-center gap-2 p-3 bg-black/5 dark:bg-black/20 rounded-xl border border-gray-200 dark:border-white/10">
            <p className="text-gray-900 dark:text-white text-sm font-mono flex-1 truncate">
              {walletAddress}
            </p>
            <button
              onClick={handleCopy}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors shrink-0"
            >
              <span className="material-symbols-outlined text-primary text-sm">
                {copied ? "check" : "content_copy"}
              </span>
            </button>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={onClose}
          className="w-full h-12 rounded-xl bg-primary text-black font-bold hover:opacity-90 transition-opacity"
        >
          Got It
        </button>
      </div>
    </div>
  );
}
